import { IFiltersResponse } from "../models/IFiltersResponse";
import site from "../site";
import AlgoliaTracking from "./algolia-tracking";
import Helpers from "./helpers";
import Logger from "./logger";
import SelectorFilter from "./selector-filter";
import ToggleBlock from "./toggle-block";
import Tooltip from "./tooltip";
import Tracking from "./tracking";
import fastdom from "fastdom";
import * as fastdomPromised from "fastdom/extensions/fastdom-promised";

declare var Mustache: any;
declare var popups: any;

export default class Filters {

	private filtersFormElement: HTMLFormElement;
	private productResultsTargetElement = document.querySelector('#rangeContentWrapper .inner .content .list') as HTMLElement;
	private usePush = true;
	private firstLoad = true;
	private filterlessCategoryMode = document.querySelectorAll('[data-page-template="FilterlessCategory"]').length;
	private loadInitialFromEndpoint = document.querySelectorAll('.js-filter-page--load-initial-from-endpoint').length;
	private mobileFiltersState: any[] = [];
	private lastChangedInput: HTMLInputElement = null;
	private productsAbortController: AbortController = null;
	private filtersAbortController: AbortController = null;
	private toggleBlockCollapsedCSSClass = 'collapsed';
	private filterItemsLimit: number = 5;
	private itemObserver: IntersectionObserver = null;
	private lastPageRequested: number;
	private allowLocationChange = true;
	private sortHasChangedFromDefault = false;
	private filtersHasChangedFromDefault = false;
	private initialSearchParams = new URLSearchParams(window.location.search);
	private productTemplate = document.getElementById('productsTemplate').innerHTML;
	private userIsLeavingPage = false;
	private isSearchResultsPage = window.location.pathname.indexOf('/search') > -1;
	private isBtuCalculatorPage = window.location.pathname.indexOf('/btu-calculator') > -1;
	private placeholderCounts: { next: number; previous: number } = { next: 0, previous: 0 };
	private productsPerPage: number;
	private maxProductsPerPage: number;
	private filterProductCount: number;
	private useUnifiedSearch: boolean = Helpers.getPageTemplate() === 'SearchIndex';
	private filtersUpdateTimeout: number | null = null;
	private pendingFilterUpdate: (() => void) | null = null;
	private minFilterPrefix: string = 'min-';
	private maxFilterPrefix: string = 'max-';
	private isShowingNoResults: boolean = false;
	private activeSite = Helpers.activeSite().toLowerCase();
	private isDrench: boolean = this.activeSite === 'drench';
	private hasFilterAccordion: boolean = this.activeSite === 'toasty';
	private quickFiltersEnabled: boolean = document.querySelector('.js-category-quick-filters') !== null;
	private defaultDebounceDelay: number = this.quickFiltersEnabled ? 1 : 1500;
	private trackSelectItemDebounced: (element: HTMLElement) => void;
	private showMoreStates: { [key: string]: boolean } = {};

	constructor(filtersFormElement: HTMLFormElement) {
		this.filtersFormElement = filtersFormElement;
		this.setup();
	}

	private setup() {
		if (this.firstLoad || this.loadInitialFromEndpoint) {
			Helpers.deferTask(() => {
				const dataFromUrl = this.getDataFromUrl();
				this.ajaxUpdate(this.filtersFormElement, dataFromUrl, true);
				this.scrollToProductIfHash();
				this.setupQuickFilters();
			});
		}
		this.attachEvents();
	}

	public reinitialize() {
		const dataFromUrl = this.getDataFromUrl();
		this.ajaxUpdate(this.filtersFormElement, dataFromUrl, true);
	}

	private setupQuickFilters() {
		if (this.quickFiltersEnabled) {
			const quickFiltersElement = document.querySelector('.js-category-quick-filters') as any;
			if (quickFiltersElement) {
				quickFiltersElement.filtersToShow = this.isDrench 
					? ['Type', 'Colour', 'Brand', 'WidthMm', 'HeightMm']
					: ['Type', 'Colour', 'WidthMm', 'HeightMm', 'FuelSource'];
				// Display name overrides for selected quick filters
				quickFiltersElement.displayNameOverrides = {
					"WidthMm": "Width",
					"HeightMm": "Height"
				};
			}
		}
	}

	private scrollToProductIfHash() {
		const windowHash = window.location.hash;
		if (windowHash) {
			if (history.scrollRestoration) {
				history.scrollRestoration = "manual";
			}
			if (!SelectorFilter.isValidIdSelector(windowHash)) {
				return;
			}
			const targetProduct = document.querySelector(windowHash);
			if (targetProduct) {
				targetProduct.scrollIntoView();
				window.addEventListener("load", () => {
					targetProduct.scrollIntoView();
				});
			}

		}
	}

	private setPageParam(value: any) {
		const params = new URLSearchParams(location.search);
		if (!params.get('page') && value === "1") {
			return;
		}
		if (params.get('page') && params.get('page') === value) {
			return;
		}
		if (parseInt(value) === 1) {
			params.delete('page');
		} else {
			params.set('page', value);
		}
		const updatedUrl = params.toString().length ? `${location.pathname}?${params.toString()}` : location.pathname;
		try {
			window.history.replaceState({}, '', updatedUrl);
		} catch (error) {
			// silently fail, updating page URL is not critical
			Logger.trackException(error);
		}
	}

	private setupItemObserver() {

		if (this.itemObserver !== null) {
			this.itemObserver.disconnect();
		}

		const items = document.querySelectorAll('.js-scroll-item') as NodeListOf<HTMLLIElement>;

		const debouncedObserver = Helpers.debounce((entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					this.allowLocationChange = false;
					const page = entry.target.getAttribute('data-from-page');
					this.setPageParam(page);
					this.allowLocationChange = true;
				}
			})
		}, 500);

		this.itemObserver = new IntersectionObserver(debouncedObserver);

		items.forEach((element) => {
			this.itemObserver.observe(element);
		});

	}

	private getPageRange() {
		const listItems = document.querySelectorAll('.js-product-results-list [data-from-page]');
		const lowestPage = listItems.length ? parseInt(listItems[0].getAttribute('data-from-page')) : 0;
		const highestPage = listItems.length ? parseInt(listItems[listItems.length - 1].getAttribute('data-from-page')) : 0;

		return {
			lowestPage: lowestPage,
			highestPage: highestPage
		}
	}

	private makeSureNotTooManyItems() {

		const lowestPage = this.getPageRange().lowestPage;
		const highestPage = this.getPageRange().highestPage;
		const shouldRemovePages = this.shouldRemovePages(this.lastPageRequested, lowestPage, highestPage);

		if (shouldRemovePages === "none") {
			return;
		}

		let itemsToRemove;

		if (shouldRemovePages === "top") {
			itemsToRemove = document.querySelectorAll('.js-limit-list-item[data-from-page="' + lowestPage.toString() + '"]');
		} else {
			itemsToRemove = document.querySelectorAll('.js-limit-list-item[data-from-page="' + highestPage.toString() + '"]');
		}

		Helpers.scopedLoop(itemsToRemove, (itemToRemove: HTMLElement, i: Number, context: any) => {
			itemToRemove.parentNode.removeChild(itemToRemove);
		}, this);

	}

	private getLoadMoreUrlForPage(page: number, nextPageUrl: string) {

		const params = new URLSearchParams(nextPageUrl);
		params.set('page', (page).toString());
		if (!this.filtersHasChangedFromDefault && !this.sortHasChangedFromDefault) {
			const keys = [...params.keys()]
			for (var key of keys) {
				if (key !== "page" && !this.initialSearchParams.get(key)) {
					params.delete(key);
				}
			};
		}
		return this.formatSearchParams(params.toString());
	}

	private formatSearchParams(url) {
		if (url.charAt(0) !== "?") {
			return "?" + url;
		}
		return url;
	}

	private shouldRemovePages(currentPage: number, lowestPage: number, highestPage: number) {
		if (!currentPage) {
			return "none";
		}
		if (currentPage === highestPage && highestPage - lowestPage > 2) {
			return "top";
		}
		if (currentPage === lowestPage && highestPage - lowestPage > 2) {
			return "bottom";
		}
		return "none";
	}

	private getDataFromUrl() {
		const currentUrl = window.location.search;
		const sort = this.getSortInput().value ? "sort=" + this.getSortInput().value: "";
		const seperator = currentUrl.indexOf('?') > -1 ? '&' : '?';
		return currentUrl + seperator + sort;
	}

	private setPageToOne() {
		const pageInputElement = this.getPageInput();
		pageInputElement.value = "1";
	}

	private debouncedFiltersUpdate(appendResults = false, prependResults = false, delay = this.defaultDebounceDelay) {
		if (this.filtersUpdateTimeout) {
			clearTimeout(this.filtersUpdateTimeout);
		}

		this.pendingFilterUpdate = () => {
			this.triggerFiltersUpdate(appendResults, prependResults);
			this.filtersUpdateTimeout = null;
			this.pendingFilterUpdate = null;
		};

		this.filtersUpdateTimeout = window.setTimeout(this.pendingFilterUpdate, delay);
	}

	private attachEvents() {

		const debouncedTagsResize = Helpers.debounce(this.setFilterTagsPosition, 250);
		const filterPageContainer = document.querySelector('.js-filter-page');
		const debouncedTrackSelectItem = Helpers.debounce((element: HTMLElement) => {
			this.trackSelectItem(element);
		}, 100);
		this.trackSelectItemDebounced = debouncedTrackSelectItem;
		const self = this;
		window.addEventListener('resize', debouncedTagsResize.bind(self));

		window.addEventListener('beforeunload', () => {
			this.userIsLeavingPage = true;
		});

		this.filtersFormElement.addEventListener('change', (e: Event) => {
			const input = e.target as HTMLInputElement;

			// Handle range filter inputs
			if (input.type === 'number' && (input.classList.contains('js-min-filter') || input.classList.contains('js-max-filter'))) {
				// Just validate, don't trigger search - user needs to click apply button
				this.validateRangeInputs(input);
				return;
			}

			// Handle all other inputs
			if (input.checked || (input.type === 'number' && input.value.trim() !== '')) {
				this.removeConflictingFilters(input);

				if (input.checked) {
					this.addFilterPlaceholder(`${input.getAttribute("name")}: ${input.value}`);
				}
			}

			this.filtersHasChangedFromDefault = true;
			// Go back to page 1 when filters change, except for page input
			if (input.getAttribute("name") !== 'page') {
				this.setPageToOne();
			}
			this.lastChangedInput = input;
			this.debouncedFiltersUpdate();

			// Analytics tracking for non-range filters
			if (this.isSearchResultsPage && input.getAttribute('value')) {
				Helpers.deferTask(() => {
					const name = input.closest('[data-filter-name]').getAttribute('data-filter-name');
					const value = input.getAttribute('value');
					AlgoliaTracking.clickedFilters(`PLP: ${AlgoliaTracking.cleanEventName(name)} Filter Clicked`, [`${encodeURI(name)}:${encodeURI(value)}`]);
				}, 250);
			}
		});

		filterPageContainer.addEventListener('click', (e: Event) => {
			const targetElement = e.target as HTMLElement;

			if (targetElement.classList.contains('js-apply-range-filter')) {
				const rangeContainer = targetElement.closest('.rangeFilter') || targetElement.closest('[data-filter-name]');
				if (rangeContainer) {
					const allMinInputs = rangeContainer.querySelectorAll('.js-min-filter') as NodeListOf<HTMLInputElement>;
					const allMaxInputs = rangeContainer.querySelectorAll('.js-max-filter') as NodeListOf<HTMLInputElement>;

					const minInput = allMinInputs.length > 0 ? allMinInputs[0] : null;
					const maxInput = allMaxInputs.length > 0 ? allMaxInputs[0] : null;

					// This ensures desktop and mobile inputs are in sync before validation
					if (minInput) {
						const minName = minInput.getAttribute('name');
						const allMinInputsByName = this.filtersFormElement.querySelectorAll(`input[name="${minName}"]`) as NodeListOf<HTMLInputElement>;
						const visibleValue = minInput.value;
						allMinInputsByName.forEach((input) => {
							input.value = visibleValue;
						});
					}

					if (maxInput) {
						const maxName = maxInput.getAttribute('name');
						const allMaxInputsByName = this.filtersFormElement.querySelectorAll(`input[name="${maxName}"]`) as NodeListOf<HTMLInputElement>;
						const visibleValue = maxInput.value;
						allMaxInputsByName.forEach((input) => {
							input.value = visibleValue;
						});
					}

					this.rehydrateUntouchedRangeInputsFromQueryString([
						minInput?.getAttribute('name'),
						maxInput?.getAttribute('name')
					]);

					// Validate inputs before applying
					let isValid = true;
					if (minInput && minInput.value.trim() !== '') {
						isValid = this.validateRangeInputs(minInput) && isValid;
					}
					if (maxInput && maxInput.value.trim() !== '') {
						isValid = this.validateRangeInputs(maxInput) && isValid;
					}

					if (isValid) {
						if (minInput) {
							this.removeConflictingFilters(minInput);
						}
						if (maxInput) {
							this.removeConflictingFilters(maxInput);
						}

						this.filtersHasChangedFromDefault = true;
						this.lastChangedInput = minInput;
						this.setPageToOne();
						this.triggerFiltersUpdate();
						// Track the filter application for analytics
						if (this.isSearchResultsPage) {
							Helpers.deferTask(() => {
								const filterName = rangeContainer.getAttribute('data-filter-name');
								if (minInput && minInput.value.trim() !== '') {
									AlgoliaTracking.clickedFilters(`PLP: ${AlgoliaTracking.cleanEventName(filterName)} Filter Applied`, [`${encodeURI(filterName)}:min=${encodeURI(minInput.value)}`]);
								}
								if (maxInput && maxInput.value.trim() !== '') {
									AlgoliaTracking.clickedFilters(`PLP: ${AlgoliaTracking.cleanEventName(filterName)} Filter Applied`, [`${encodeURI(filterName)}:max=${encodeURI(maxInput.value)}`]);
								}
							}, 100);
						}
					}
				}
				e.preventDefault();
				return;
			}

			// Applied filter tags
			if (targetElement.classList.contains('js-applied-filter')) {
				const urlWithoutFacet = targetElement.getAttribute('href');
				if (urlWithoutFacet) {
					history.pushState({}, this.getTitle(), urlWithoutFacet);
					e.preventDefault();
					return;
					}

				let dataFacetId = targetElement.getAttribute('data-facet-id');
				if (dataFacetId) {
					let dataFacetIds = dataFacetId.split(',');

					for (var i = 0; i < dataFacetIds.length; i++) {
						let appliedFacets = document.querySelectorAll(`[data-id^=${CSS.escape(dataFacetIds[i])}]`) as NodeListOf<HTMLInputElement>;

						if (appliedFacets) {
							for (let i = 0; i < appliedFacets.length; i++) {
								this.deselectFacet(appliedFacets[i]);
							}
						}
					}
				}

				if (targetElement.closest('.listItem')) {
					targetElement.closest('.listItem').remove()
				}
				this.setPageToOne();
				this.triggerFiltersUpdate();
				e.preventDefault();
			}

			// Clear all above e.g. ratings
			if (targetElement.classList.contains('js-clear-above')) {
				const group = targetElement.closest('.content');
				this.deselectFacets(group);
				this.setPageToOne();
				this.triggerFiltersUpdate();
				targetElement.classList.add('filterClearAboveDone');
				e.preventDefault();
			}

			// Clear all filters
			if (targetElement.classList.contains('clearAllFilters')) {
				const filters = document.querySelector('.js-applied-filters-inner');
				if (filters) {
					filters.remove();
				}
				this.deselectFacets(this.filtersFormElement);
				this.lastChangedInput = null;
				this.setPageToOne();
				this.triggerFiltersUpdate();
				this.filterProductCount = this.productsPerPage;
				e.preventDefault();
			}

			// Remove last filter
			if (targetElement.classList.contains('js-remove-last-filter')) {
				this.removeLastFilter();
				e.preventDefault();
			}

			// Load more
			if (targetElement.classList.contains('js-load-more')) {

				const queryString = targetElement.getAttribute('href');
				const params = new URLSearchParams(queryString);

				this.changePage(params.get('page'), true, false);
				e.preventDefault();
				return false;

			}

			// Load previous
			if (targetElement.classList.contains('js-load-previous')) {

				const queryString = targetElement.getAttribute('href');
				const params = new URLSearchParams(queryString);

				this.changePage(params.get('page'), false, true);
				e.preventDefault();
				return false;

			}

			// Skip to results accessibility link
			if (targetElement.classList.contains('js-filter-skip-to-results')) {
				e.preventDefault();
				this.closeFilterSlideOutIfOpen();
				this.focusOnFirstResult();
			}

			// Track product link clicks
			if (targetElement.classList.contains('js-product-link') || targetElement.closest('.js-product-link')) {
				this.trackSelectItemDebounced(targetElement);
				const productID = targetElement.closest('[data-test-product-id]').getAttribute('data-test-product-id');
				this.allowLocationChange = false;
				history.pushState('', '', '#product-' + productID);
			}

		});

		this.filtersFormElement.addEventListener('input', (e: Event) => {
			const input = e.target as HTMLInputElement;
			if (input.type === 'number' && (input.classList.contains('js-min-filter') || input.classList.contains('js-max-filter'))) {
				this.validateRangeInputs(input, false);
			}
		});

		filterPageContainer.addEventListener('change', (e: Event) => {
			const targetElement = e.target as HTMLInputElement;
			if (targetElement.matches('select[name="sort"]')) {
				this.sortHasChangedFromDefault = true;
				const sortInput = this.filtersFormElement.querySelector('input[name="sort"]') as HTMLInputElement;
				sortInput.value = targetElement.value;
				this.filterProductCount = this.placeholderCounts.next;
				this.setPageToOne();
				this.triggerFiltersUpdate();
			}
		});

		window.addEventListener('locationchange', () => {
			if (this.allowLocationChange) { // The IntersectionObserver behaviour added for load more triggers this update consistently without the boolean check
				this.ajaxUpdate(this.filtersFormElement, this.getDataFromUrl());
			}
		});

		this.filtersFormElement.addEventListener('blur', (e: FocusEvent) => {
			if (this.pendingFilterUpdate && !this.filtersUpdateTimeout) {
				let next = (e.relatedTarget || document.activeElement) as HTMLElement;

				const isStayingInFiltersForm = next && this.filtersFormElement.contains(next);

				if (!isStayingInFiltersForm) {
					this.filtersUpdateTimeout = window.setTimeout(this.pendingFilterUpdate, 1500);
				}
			}
		}, true);

		// Handle Enter key press on range filter inputs
		this.filtersFormElement.addEventListener('keydown', (e: KeyboardEvent) => {
			if (e.key === 'Enter') {
				const input = e.target as HTMLInputElement;
				if (input.type === 'number' && (input.classList.contains('js-min-filter') || input.classList.contains('js-max-filter'))) {
					const rangeContainer = input.closest('.rangeFilter') || input.closest('[data-filter-name]');
					if (rangeContainer) {
						const applyButton = rangeContainer.querySelector('.js-apply-range-filter') as HTMLButtonElement;
						if (applyButton) {
							applyButton.click();
							e.preventDefault();
						}
					}
				}
			}
		});
	}

	private focusOnFirstResult() {
		const firstResult = document.querySelector('#rangeContentWrapper .js-product a') as HTMLLinkElement;
		if (firstResult) {
			fastdom.mutate(() => {
				firstResult.focus();
			});
		}
	}

	private closeFilterSlideOutIfOpen() {
		const slideOutElement = document.querySelector('.js-filter-slide-out') as HTMLElement;
		const closeButton = document.querySelector('.js-filter-slide-close') as HTMLButtonElement;
		const isSlideOutOpen = slideOutElement
			&& (slideOutElement.classList.contains('active') || slideOutElement.classList.contains('is-active'));

		if (!isSlideOutOpen || !closeButton) {
			return;
		}

		fastdom.mutate(() => {
			closeButton.click();
		});
	}

	private focusOnFirstNewResult() {
		const highestPage = this.getPageRange().highestPage;
		const firstNewResult = document.querySelector('.js-limit-list-item[data-from-page="' + highestPage.toString() + '"] a') as HTMLElement;
		if (firstNewResult) {
			fastdom.mutate(() => {
				firstNewResult.focus();
			});
		}
	}

	private triggerFiltersUpdate(appendResults = false, prependResults = false) {
		if (!this.isFormValid()) {
			return;
		}

		if (this.productsAbortController) {
			this.productsAbortController.abort();
			this.productsAbortController = null;
		}

		if (this.filtersAbortController) {
			this.filtersAbortController.abort();
			this.filtersAbortController = null;
		}

		this.allowLocationChange = false;
		const formData = '?' + this.serializeFormData();
		try {
			if (this.usePush) {
				history.pushState({form: formData}, this.getTitle(), window.location.pathname + formData);
			} else {
				history.replaceState(null, null, window.location.pathname + formData);
			}
		} catch (error) {
			// silently fail, updating page URL is not critical
			Logger.trackException(error);
		}
		this.ajaxUpdate(this.filtersFormElement, formData, false, appendResults, prependResults);
	}

	private setIfFiltersShouldBeScrollable() {

		if (document.querySelector('.js-filter-b-test')) {
			return;
		}

		const filterContentElements = this.filtersFormElement.querySelectorAll('.js-filter-content');

		for (let i = 0, len = filterContentElements.length; i < len; i++) {
			const wrapper = filterContentElements[i];
			if (wrapper.querySelectorAll('.level1').length > 6) {
				wrapper.classList.add('scrollable');
			}
			wrapper.classList.add('filterScrollableDone');
		}

	}

	private saveMobileFiltersOpenState() {

		this.mobileFiltersState = [];

		const toggleBlockElements = this.filtersFormElement.querySelectorAll('.js-toggle-block');

		for (let i = 0, len = toggleBlockElements.length; i < len; i++) {
			const currentToggleBlock = toggleBlockElements[i];
			const currentFilterName = currentToggleBlock.querySelector('[data-toggle-value]')?.getAttribute('data-toggle-value');
			const showMoreOpen = currentToggleBlock.querySelector('.mShowMore .link')
				? currentToggleBlock.querySelector('.mShowMore .link').classList.contains('mShowMore--expanded')
				: false;
			this.mobileFiltersState.push({
				name: currentToggleBlock.querySelector('[data-toggle-value]').getAttribute('data-toggle-value'),
				isCollapsed: currentToggleBlock.querySelector('.js-toggle-title').classList.contains(this.toggleBlockCollapsedCSSClass),
				showMoreOpen: showMoreOpen
			});

			if (currentFilterName) {
				this.saveShowMoreState(currentFilterName, showMoreOpen);
			}
		}

	}

	private setMobileFiltersOpenState() {

		for (let i = 0, len = this.mobileFiltersState.length; i < len; i++) {
			const currentFilter = this.mobileFiltersState[i];
			const currentFilterToggleValue = this.filtersFormElement.querySelector('[data-toggle-value="' + currentFilter.name + '"]');
			if (!currentFilterToggleValue) {
				continue;
			}
			const currentFilterContainer = currentFilterToggleValue.closest('.js-toggle-block');
			const toggleTitle = currentFilterContainer.querySelector('.js-toggle-title');
			const toggleContent = currentFilterContainer.querySelector('.js-toggle-content');
			const stateInput = currentFilterContainer.querySelector('.js-toggle-expanded') as HTMLInputElement;
			const isExpanded = !currentFilter.isCollapsed;
			if (currentFilter.isCollapsed) {
				toggleTitle.classList.add(this.toggleBlockCollapsedCSSClass);
				toggleContent.classList.add(this.toggleBlockCollapsedCSSClass);
			} else {
				toggleTitle.classList.remove(this.toggleBlockCollapsedCSSClass);
				toggleContent.classList.remove(this.toggleBlockCollapsedCSSClass);
			}
			toggleTitle.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
			if (stateInput) {
				stateInput.value = isExpanded ? currentFilter.name : '';
			}
			const showMoreButton = currentFilterContainer.querySelector('[data-show-more-toggle]') as HTMLElement;
			const showMoreLink = showMoreButton ? showMoreButton.querySelector('[data-show-more-link]') as HTMLElement : null;
			if (showMoreButton && showMoreLink) {
				this.setShowMoreLinkState(showMoreButton, currentFilter.showMoreOpen);
			}
		}

	}

	private deselectFacets(container: Element) {
		const inputs = container.querySelectorAll('input');
		for (let i = 0, len = inputs.length; i < len; i++) {
			this.deselectFacet(inputs[i]);
		}
	}

	private deselectFacet(input: HTMLInputElement) {
		// never deselect the search term input
		if (input.id === "term") {
			return;
		}
		if (input.type === "checkbox") {
			input.checked = false;
			input.removeAttribute('checked');
		} else {
			input.value = "";
		}
	}

	private removeConflictingFilters(changedInput: HTMLInputElement) {
		const inputName = changedInput.getAttribute('name');

		const isRangeFilter = inputName.startsWith(this.minFilterPrefix) || inputName.startsWith(this.maxFilterPrefix);

		let baseFilterName = inputName;
		if (isRangeFilter) {
			baseFilterName = inputName
				.replace(this.minFilterPrefix, '')
				.replace(this.maxFilterPrefix, '');
		}

		const allInputs = this.filtersFormElement.querySelectorAll('input[type="checkbox"], input[type="radio"], input[type="number"]');

		allInputs.forEach((input: HTMLInputElement) => {
			if (input === changedInput) {
				return;
			}

			const currentName = input.getAttribute('name');
			const currentIsRange = currentName.startsWith(this.minFilterPrefix) || currentName.startsWith(this.maxFilterPrefix);

			let currentBaseName = currentName;
			if (currentIsRange) {
				currentBaseName = currentName
					.replace(this.minFilterPrefix, '')
					.replace(this.maxFilterPrefix, '');
			}

			if (currentBaseName === baseFilterName && isRangeFilter !== currentIsRange) {
				this.deselectFacet(input);
			}
		});
	}

	private getTitle() {
		return document.title;
	}

	private getPageInput() {
		return this.filtersFormElement.querySelector('input[name="page"]') as HTMLInputElement
	}

	private getSortInput() {
		return this.filtersFormElement.querySelector('input[name="sort"]') as HTMLInputElement
	}

	private changePage(page: any, appendResults = false, prependResults = false) {
		const pageInput = this.getPageInput();
		pageInput.value = page;
		this.triggerFiltersUpdate(appendResults, prependResults);
	}

	private ajaxUpdate(form: HTMLElement, formData: string, firstLoad = false, appendResults = false, prependResults = false) {

		Helpers.deferTask(() => {

			this.saveMobileFiltersOpenState();

			// scroll up
			if (!this.filterlessCategoryMode && !firstLoad && !appendResults && !prependResults) {
				this.scrollToTopOfResults();
			}

			if (!firstLoad) {
				fastdom.mutate(() => {
					popups.loader();
					if (this.productResultsTargetElement && !appendResults && !prependResults) {
						this.productResultsTargetElement.innerHTML = '';
						this.addPlaceholderProducts(appendResults, prependResults, true);
					}
				});
			}

			// get the data
			if (firstLoad) {
				if(this.loadInitialFromEndpoint) {
						this.getProducts(form, formData, false, appendResults, prependResults);
						this.getFilters(form, formData, false);
				} else {
					this.getInitialDataFromPage();
					if (!this.useUnifiedSearch) {
						this.getFilters(form, formData, firstLoad);
					}
				}

			} else if (this.useUnifiedSearch) {
				this.getProductsAndFilters(form, formData, firstLoad, appendResults, prependResults);
			} else {
				this.getProducts(form, formData, firstLoad, appendResults, prependResults);
				this.getFilters(form, formData, firstLoad);
			}

			this.allowLocationChange = true;

		});

	}

	private scrollToTopOfResults() {
		const scrollAfterDataElement = document.querySelector('.js-filter-scroll-after-change') as HTMLElement;
		const elementToScrollTo = scrollAfterDataElement ? scrollAfterDataElement : document.getElementById('content') as HTMLElement;
		fastdom.measure(() => {
			const scrollOffset = Helpers.getOffset(elementToScrollTo).top;
			fastdom.mutate(() => {
				const scrollOffsetVariant = window.innerWidth < 640 ? 50 : 100; // 640 matches the $bpTabletLarge breakpoint
				if (window.scrollY < scrollOffset - scrollOffsetVariant || site.win.scrollTop() > scrollOffset + scrollOffsetVariant) {
					window.scrollTo({
						top: scrollOffset,
						left: 0,
						behavior: 'smooth'
					});
					setTimeout(() => {
						const resetHeaderEvent = Helpers.createCustomEventForDispatch('resetHeader');
						document.dispatchEvent(resetHeaderEvent);
					}, 400);
				}
			});
		});
	}

	private getProductsAndFilters(form: any, formData: string, firstLoad: boolean, appendResults = false, prependResults = false) {
		this.getProducts(form, formData, firstLoad, appendResults, prependResults, true);
	}

	private getInitialDataFromPage() {

		this.firstLoad = false;

		// product data
		const initialDataElement = document.querySelector('.js-filters-initial-data');
		if (!initialDataElement) {
			return;
		}
		let initialData: any;
		try {
			initialData = JSON.parse(initialDataElement.innerHTML);
		} catch (error) {
			Logger.trackException(error);
			return;
		}
		this.placeholderCounts = this.calculatePlaceholders(initialData);
		this.productsPerPage = initialData.PageMeta.PerPage;
		this.maxProductsPerPage = this.productsPerPage * 3;
		this.filterProductCount = this.productsPerPage; // Initialize filter product count
		AlgoliaTracking.updateAlgoliaFromSearchResponse(initialData.AlgoliaAttributes);
		this.trackListView(initialData);
		Tooltip.init(false);

		// facets
		if (this.useUnifiedSearch) {
			this.handleFilterResults(initialData, true);
		}

		if (!this.useUnifiedSearch && !initialData.Products.length) {
			this.showNoResultsMessage();
		}

	}

	private getProducts(form: any, formData: string, firstLoad: boolean, appendResults = false, prependResults = false, unifiedSearch = false) {

		// abort previous requests
		if (this.productsAbortController) {
			this.productsAbortController.abort();
		}
		this.productsAbortController = new AbortController();
		const signal = this.productsAbortController.signal;

		this.addPlaceholderProducts(appendResults, prependResults, false);

		if (unifiedSearch) {
			this.setFiltersLoading();
		}

		fetch(this.formatRequestUrl(form.getAttribute('data-products-json-url'), formData), {
			signal: signal
		})
			.then((response: any) => {
				if (response.ok) {
					return response.json();
				} else {
					throw new Error(response as any);
				}
			})
			.then((data) => {

				this.handleProductResults(data, firstLoad, appendResults, prependResults);
				if (unifiedSearch) {
					this.handleFilterResults(data, firstLoad);
				}

			})
			.catch((error) => this.showRequestError(error));
	}

	private handleProductResults(data: any, firstLoad: boolean, appendResults: boolean, prependResults: boolean) {

		this.lastPageRequested = data.PageMeta.Page;

		this.removeErrors();

		this.checkForNoResults(data, appendResults, prependResults);

		this.templateProductResults(data, firstLoad, appendResults, prependResults);

		if (appendResults) {
			this.scrollToFirstAppendedProducts();
		}

		AlgoliaTracking.updateAlgoliaFromSearchResponse(data.AlgoliaAttributes);

		// hide loader
		fastdom.mutate(() => {
			popups.allow = true;
			popups.close();
		});

		// observe new items
		this.setupItemObserver();

		if (this.productResultsTargetElement && !firstLoad) {
			fastdom.mutate(() => {
				this.productResultsTargetElement.style.display = 'grid';
			});
		}

		if (this.filterlessCategoryMode) this.scrollToTop();

		// reinitialise components
		Tooltip.init(false);

		if (this.lastChangedInput && this.lastChangedInput.closest('.mFilter')) {
			const filterElement = this.lastChangedInput.closest('.mFilter') as HTMLElement;

			if (window.matchMedia('(max-width: 980px)').matches) {
				filterElement.scrollIntoView();
			} else if (this.activeSite === 'toasty') {
				const waitingToRenderTime = 250;
				setTimeout(() => {
					const filterName = filterElement.getAttribute('data-filter-name');
					const updatedFilterGroup = document.querySelector('[data-filter-name="' + filterName + '"]');
					if (updatedFilterGroup) {
						updatedFilterGroup.scrollIntoView({ behavior: 'smooth', block: 'center' });
						if (this.lastChangedInput) {
							const lastChangedInputInUpdatedGroup = updatedFilterGroup.querySelector(`[value="${this.lastChangedInput.getAttribute('value')}"]`) as HTMLElement;
							if (lastChangedInputInUpdatedGroup) {
								lastChangedInputInUpdatedGroup.focus();
							}
						}
					}
				}, waitingToRenderTime);
			}
		}
		this.productsAbortController = null;

		if (!firstLoad && Helpers.isUsingKeyboard()) {
			if (appendResults) {
				this.focusOnFirstNewResult();
			} else {
				this.focusOnFirstResult();
			}

		}

		this.trackListView(data);

		fastdom.mutate(() => {
			this.updateCanonicalLink(data.PageMeta.Page);
			this.updateRelNextAndPrevious(data.PageMeta.Page, data.PageMeta.HasNextPage, data.PageMeta.HasPreviousPage);
		});

	}

	private getFilters(form: any, formData: string, firstLoad: boolean) {

		// abort previous requests
		if (this.filtersAbortController) {
			this.filtersAbortController.abort();
		}
		this.filtersAbortController = new AbortController();
		const signal = this.filtersAbortController.signal;

		this.setFiltersLoading();

		fetch(this.formatRequestUrl(form.getAttribute('data-facets-json-url'), formData), {
			signal: signal
		})
			.then((response: any) => {
				if (response.ok) {
					return response.json();
				} else {
					throw new Error(response as any);
				}
			})
			.then((data) => this.handleFilterResults(data, firstLoad))
			.catch((error) => this.showRequestError(error));

	}

	private handleFilterResults(data: IFiltersResponse, firstLoad: boolean) {
		this.markZeroResultFilters(data);

		const filtersUpdatedEvent = Helpers.createCustomEventForDispatch('filtersUpdated', data);
		document.dispatchEvent(filtersUpdatedEvent);

		const fastdomPromise = fastdom.extend(fastdomPromised);

		if (firstLoad && !this.isDesktopLayout()) {
			data = this.setFiltersToClosed(data);
		}

		fastdomPromise.mutate(() => {
			this.templateFilterResults(data, firstLoad);
		}).then(() => {

			this.setMobileFiltersOpenState();
			Tooltip.init(false);

			this.showMoreToggles();
			new ToggleBlock();

			this.setFilterTagsPosition();
			this.setIfFiltersShouldBeScrollable();

			this.setFiltersLoaded();

			this.trackViewFilters(data);

			this.saveAppliedFilters(data);
		});
	}

	private scrollToFirstAppendedProducts() {
		const highestPage = this.getPageRange().highestPage;
		const firstNewElement = document.querySelector('.js-limit-list-item[data-from-page="' + highestPage.toString() + '"]');
		const offset = -50;
		const newYPosition = firstNewElement.getBoundingClientRect().top + window.pageYOffset + offset;
		window.scrollTo(0, newYPosition);
	}

	private trackViewFilters(data) {

		if (!this.isSearchResultsPage) {
			return;
		}

		const filterElements = document.querySelectorAll('[data-filter-name] li label');

		filterElements.forEach((filterElement) => {
			const observer = new IntersectionObserver((entries) => {
				entries.forEach(entry => {
					if (entry.isIntersecting) {
						const filterName = filterElement.closest('[data-filter-name]').getAttribute('data-filter-name');
						const filterId = filterElement.getAttribute('for');
						const currentFilter = data.Filters.find((filter) => filter.DisplayName === filterName);
						const filterValue = currentFilter.Values.find((item) => item.Id === filterId);
						AlgoliaTracking.viewedFilters(`PLP: ${AlgoliaTracking.cleanEventName(filterName)} Filter Viewed`, [`${encodeURI(currentFilter.Name)}:${encodeURI(filterValue.Text)}`]);
						observer.disconnect();
					}
				});
			});
			observer.observe(filterElement);
		});

	}

	private saveAppliedFilters(data) {
		try {
			if (data.AppliedFilters.length) {
				const appliedFilters = data.AppliedFilters.map((filter) => AlgoliaTracking.cleanEventName(filter.Name.split(':')[0].trim()) + ':' + encodeURI(filter.Name.split(':')[1].trim()));
				sessionStorage.setItem('appliedFilters', JSON.stringify(appliedFilters));
			} else {
				sessionStorage.removeItem('appliedFilters');
			}

		} catch (e) {
			console.log(e);
		}

	}

	/**
	 * This is needed for the filters/load more AB test to make sure the filters match live behaviour
	 * */
	private setFiltersToClosed(data) {
		for (var i = 0; i < data.Filters.length; i++) {
			const filter = data.Filters[i];
			const isDeliveryFilter = filter.Name === 'productstocklevel';
			filter.IsExpanded = isDeliveryFilter;
		}
		return data;
	}

	private setFiltersLoading() {
		const filters = document.querySelectorAll('.mFilter');

		Helpers.scopedLoop(filters, (filter: HTMLElement, i: Number, context: any) => {
			fastdom.mutate(() => {
				filter.classList.add('mFilter--placeholder');
			});
		}, this);
	}

	private setFiltersLoaded() {
		const filters = document.querySelectorAll('.mFilter');
		Helpers.scopedLoop(filters, (filter: HTMLElement, i: Number, context: any) => {
			fastdom.mutate(() => {
				filter.classList.remove('mFilter--placeholder');
			});
		}, this);
	}

	private checkForNoResults(data: any, appendResults: boolean, prependResults: boolean) {
		if (!appendResults && !prependResults && data.Products?.length === 0 && data.PageMeta?.TotalResults === 0) {
			const hasAppliedFilters = this.filtersHasChangedFromDefault || (data.AppliedFilters?.length > 0);

			if (hasAppliedFilters || this.isBtuCalculatorPage) {
				this.showNoResultsMessage();
				if (this.useUnifiedSearch) {
					this.templateFilterResults(data, false);
					this.setFilterTagsPosition();
				}
			}
		}
	}

	private btuNoResultsLink() {
		return this.isDrench ? '/c/heating' : `/c/radiators/all-radiators`;
	}

	private showNoResultsMessage() {
		this.removeErrors();

		this.isShowingNoResults = true;

		fastdom.mutate(() => {
			popups.allow = true;
			popups.close();
		});

		fastdom.mutate(() => {
			if (!document.querySelector('.js-filters-no-results')) {
				const hasLastFilter = this.lastChangedInput !== null;
				const removeLastFilterOption = hasLastFilter
					? `<a href="#" data-test="filters-no-results-remove-last-filter" class="js-remove-last-filter">removing the last filter</a> or `
					: '';

				let noResultsMessage = "";

				if (this.isBtuCalculatorPage) {
					const btuToElement = document.querySelector('.js-btu-to');
					let btuTo = 0;
					if (btuToElement) {
						btuTo = parseInt(btuToElement.textContent.replace(/\D/g, ''), 10);
					}
					if (btuTo > 6000) {
						noResultsMessage = `
						<div class="js-filters-no-results c-cms mFilter__no-results" data-test="filters-no-results">
							<div class="mFilter__no-results-inner">
								<h3 class="mFilter__no-results-title">No products found</h3>
								<p class="mFilter__no-results-text" data-test="btu-no-results-text">We couldn't find an exact match for your total BTU requirement. You may need more than one radiator to hit that target.</p>
								<p class="mFilter__no-results-actions"><a href="${this.btuNoResultsLink()}">Browse our range</a> to find the right combination!</p>
							</div>
						</div>
					`;
					} else {
						noResultsMessage = `
						<div class="js-filters-no-results c-cms mFilter__no-results" data-test="filters-no-results">
							<div class="mFilter__no-results-inner">
								<h3 class="mFilter__no-results-title">No products found</h3>
								<p class="mFilter__no-results-text" data-test="btu-no-results-text">We couldn't find an exact match for your total BTU requirement. Try checking your room details to see more results.</p>
								<p class="mFilter__no-results-actions"><a href="${this.btuNoResultsLink()}">Browse our range</a></p>
							</div>
						</div>
					`;
					}
				} else {
					noResultsMessage = `
						<div class="js-filters-no-results c-cms mFilter__no-results" data-test="filters-no-results">
							<div class="mFilter__no-results-inner">
								<h3 class="mFilter__no-results-title">No products found</h3>
								<p class="mFilter__no-results-text">We couldn't find any products matching your current filters.</p>
								<p class="mFilter__no-results-actions">Try ${removeLastFilterOption}<a href="#" data-test="filters-no-results-clear-all-filters" class="clearAllFilters">clearing all filters</a> to see more results.</p>
							</div>
						</div>
					`;
				}

				this.productResultsTargetElement.insertAdjacentHTML('afterbegin', noResultsMessage);
			}

			const pagerCountElements = document.querySelectorAll('.c-pager__count');
			const loadElements = document.querySelectorAll('.c-load');
			const mobileCountElements = document.querySelectorAll('.js-filter-mobile-show');

			pagerCountElements.forEach(element => {
				(element as HTMLElement).classList.add('u-hide');
			});

			loadElements.forEach(element => {
				(element as HTMLElement).classList.add('u-hide');
			});

			mobileCountElements.forEach(element => {
				(element as HTMLElement).innerHTML = 'Show <b>0</b> Products';
			});
		});
	}

	private showRequestError(error: Error) {
		console.log(error);
		if (error?.name === 'AbortError') {
			return;
		}

		if (this.userIsLeavingPage) {
			return;
		}

		this.setFiltersLoaded();

		if (this.isShowingNoResults) {
			return;
		}

		this.removeErrors();

		let errorMessage = 'Sorry, there was an error when filtering. Please try again.';

		if (error.message && error.message.includes('fetch')) {
			errorMessage = 'Unable to connect to the server. Please check your internet connection and try again.';
		} else if (error.message && error.message.includes('timeout')) {
			errorMessage = 'The request took too long to complete. Please try again.';
		} else if (error.message && error.message.includes('500')) {
			errorMessage = 'Server error occurred. Please try again in a few moments.';
		}

		fastdom.mutate(() => {
			if (!document.querySelector('.js-filters-error')) {
				this.productResultsTargetElement.insertAdjacentHTML('beforebegin', `<p class="formError js-filters-error">${errorMessage}</p>`)
			}
			popups.loader(true);
			const popupFadeElement = document.getElementById('popupFade') as HTMLDivElement;
			if (popupFadeElement) {
				popupFadeElement.style.display = 'none';
			}
			if (this.productResultsTargetElement) {
				this.productResultsTargetElement.style.display = 'block';
			}
		});
	}

	private formatRequestUrl(baseUrl: string, formData: string) {
		if (baseUrl.indexOf('?') !== -1 && (formData != null && formData.indexOf('?') === 0)) {
			formData = formData.substring(1);
		}
		return baseUrl + '&' + formData;
	}

	private addFilterPlaceholder(contents) {
		const placeholderProduct = `<li class="listItem js-placeholder-filter"><div class="link js-applied-filter">${contents}</div></li>`;
		const placeholderContainer = `<div class="mFilterTags js-applied-filters-inner mFilterTags--initialized"><ul class="list">${placeholderProduct}</ul></div>`;
		const filterContainer = document.querySelector('.js-applied-filters-container');
		const filterList = document.querySelector('.js-applied-filters-inner .list');

		if (filterList) {
			filterList.insertAdjacentHTML('beforeend', placeholderProduct);
		} else {
			this.setFilterTagsPosition();
			filterContainer.insertAdjacentHTML('beforeend', placeholderContainer);
		}
	}

	private removePlaceholders(targetElement: HTMLElement): void {
		const placeholders = targetElement.querySelectorAll('.js-placeholder-item');
		placeholders.forEach(placeholder => placeholder.remove());
	}

	private calculatePlaceholders(data) {
		const {ResultsEnd, ResultsStart, PerPage, TotalResults, HasNextPage, HasPreviousPage} = data.PageMeta;

		let placeholdersForNext = 0;
		let placeholdersForPrevious = 0;

		if (HasNextPage) {
			placeholdersForNext = Math.min(PerPage, TotalResults - ResultsEnd);
		}

		if (HasPreviousPage) {
			placeholdersForPrevious = Math.min(PerPage, ResultsStart - 1);
		}

		return {
			next: placeholdersForNext,
			previous: placeholdersForPrevious,
		};
	}

	private getProductCountForCategory(categoryName: string): number {
		const categoryContainer = document.querySelector(
			`.mFilter[data-filter-name="${categoryName}"]`
		);

		if (!categoryContainer) {
			return 0;
		}

		const checkedFilters = categoryContainer.querySelectorAll<HTMLInputElement>(
			'input[type="checkbox"]:checked'
		);

		let totalProducts = 0;

		checkedFilters.forEach((filter) => {
			const countElement = filter.getAttribute("data-filter-option-hit-count");

			if (countElement) {
				totalProducts += Number(countElement);
			}
		});

		return totalProducts;
	}


	private addPlaceholderProducts(appendResults = false, prependResults = false, filters = false) {
		const placeholderProduct = `<li class="level1 js-placeholder-item"><div class="c-product-card__placeholder"></div></li>`;
		let productsCount = this.productResultsTargetElement.querySelectorAll('.js-scroll-item').length;
		let placeholdersHTML = '';

		if (appendResults) {
			// Generate placeholders for "next"
			for (let i = 0; i < this.placeholderCounts.next; i++) {
				placeholdersHTML += placeholderProduct;
			}
		} else if (prependResults) {
			// Generate placeholders for "previous"
			for (let i = 0; i < this.placeholderCounts.previous; i++) {
				placeholdersHTML += placeholderProduct;
			}
		} else if (filters) {
			for (let i = 0; i < this.filterProductCount; i++) {
				placeholdersHTML += placeholderProduct;
			}
		}

		if (appendResults && productsCount !== this.maxProductsPerPage && !filters) {
			this.productResultsTargetElement.insertAdjacentHTML('beforeend', placeholdersHTML);
		} else if (prependResults && productsCount !== this.maxProductsPerPage && !filters) {
			this.productResultsTargetElement.insertAdjacentHTML('afterbegin', placeholdersHTML);
		} else if (filters) {
			this.productResultsTargetElement.insertAdjacentHTML('beforeend', placeholdersHTML);
		}
		return;

	}

	private templateProductResults(data: any, firstLoad: boolean, appendResults = false, prependResults = false) {

		if (this.productResultsTargetElement) {
			if (appendResults) {
				this.productResultsTargetElement.insertAdjacentHTML('beforeend', Mustache.render(this.productTemplate, data));
				this.placeholderCounts = this.calculatePlaceholders(data);
			} else if (prependResults) {
				this.productResultsTargetElement.insertAdjacentHTML('afterbegin', Mustache.render(this.productTemplate, data));
				this.placeholderCounts = this.calculatePlaceholders(data);
			} else {
				this.productResultsTargetElement.innerHTML = Mustache.render(this.productTemplate, data);
			}

			this.removePlaceholders(this.productResultsTargetElement);
		}

		this.makeSureNotTooManyItems();

		// Set pager
		const lowestPage = this.getPageRange().lowestPage;
		const highestPage = this.getPageRange().highestPage;

		function resultsStart(data) {
			var numberOfPagesBefore = Math.abs(1 - lowestPage);
			return (numberOfPagesBefore * data.PerPage) + 1;
		}

		function resultsEnd(data) {
			var previousPagesTotal = (highestPage - 1) * data.PerPage;
			var highPageTotal = document.querySelectorAll('[data-from-page="' + highestPage + '"]').length;
			return previousPagesTotal + highPageTotal;
		}

		data.PageMeta.ResultsStart = resultsStart(data.PageMeta);
		data.PageMeta.ResultsEnd = resultsEnd(data.PageMeta);

		const pagerTemplate = document.getElementById('pagerTemplate').innerHTML;
		const pagerElements = document.querySelectorAll('.mPager');
		if (pagerElements.length) {
			for (var i = 0; i < pagerElements.length; i++) {
				pagerElements[i].innerHTML = Mustache.render(pagerTemplate, data.PageMeta);
			}
		}

		const filterTotalTemplate = document.getElementById('filterTotalTemplate');
		const filterSubmitElement = document.querySelector('.filterSubmit');
		if (filterSubmitElement && filterTotalTemplate) {
			filterSubmitElement.innerHTML = Mustache.render(filterTotalTemplate.innerHTML, data);
		}

		// load more / previous START

		const loadTemplate = document.getElementById('loadTemplate') ? document.getElementById('loadTemplate').innerHTML : false;
		const loadMoreElement = document.querySelector('.c-load--more');
		const loadPreviousElement = document.querySelector('.c-load--previous');

		if (loadMoreElement && loadTemplate) {

			let loadMoreData = data.PageMeta;
			loadMoreData.ShowSortOptions = false;
			loadMoreData.HasNextPage = data.PageMeta.ResultsEnd !== data.PageMeta.TotalResults;
			loadMoreData.NextPageUrl = this.getLoadMoreUrlForPage(highestPage + 1, data.PageMeta.NextPageUrl);
			loadMoreElement.innerHTML = Mustache.render(loadTemplate, loadMoreData);

		}

		if (loadPreviousElement && loadTemplate) {
			const previousPageNumber = lowestPage - 1;
			if (previousPageNumber < 1) {
				loadPreviousElement.innerHTML = '';
			} else {
				let loadPreviousData = data.PageMeta;
				loadPreviousData.ShowSortOptions = true;
				loadPreviousData.PreviousPageUrl = this.getLoadMoreUrlForPage(lowestPage - 1, data.PageMeta.PreviousPageUrl);
				loadPreviousElement.innerHTML = Mustache.render(loadTemplate, loadPreviousData);
			}
		}

	}

	private templateFilterResults(data: any, firstLoad: boolean) {
		if (this.filterlessCategoryMode) {
			return;
		}

		this.markZeroResultFilters(data);

		let filtersHTML = '';
		for (var i = 0; i < data.Filters.length; i++) {
			const filter = data.Filters[i];

			filter.isRange = filter.DataType === 'int' || filter.DataType === 'decimal';
			filter.unit = '';
			filter.isPriceFilter = false;
			filter.IsDrench = this.isDrench;
			filter.HasFilterAccordion = this.hasFilterAccordion;

			if (!filter.unit && /price/i.test(filter.DisplayName)) {
				filter.unit = '£';
				filter.isPriceFilter = true;
			}

			let filterInfo = this.filterInfo(filter);
			if (filterInfo.isMinMax) {
				let parentFilterName = filter.Name
					.replace(this.minFilterPrefix, '')
					.replace(this.maxFilterPrefix, '');

				if (this.matchingFilter(parentFilterName, data.Filters)) {
					continue;
				}

				filter.Name = parentFilterName;

				if (filterInfo.isMin) {
					filter.DisplayName = filter.DisplayName.replace('Min ', '');
					this.setMinValues(filter, filter);
				} else if (filterInfo.isMax) {
					filter.DisplayName = filter.DisplayName.replace('Max ', '');
					this.setMaxValues(filter, filter);
				}

				filter.Values = [];
			}

			if (filter.isRange) {
				let minFilter = data.Filters.find((f) => f.Name === this.minFilterPrefix + filter.Name);
				let maxFilter = data.Filters.find((f) => f.Name === this.maxFilterPrefix + filter.Name);

				if (minFilter) {
					this.setMinValues(minFilter, filter);
				}

				if (maxFilter) {
					this.setMaxValues(maxFilter, filter);
				}
			}

			filter.hasMoreThanLimit = filter.Values && filter.Values.length > this.filterItemsLimit;
			filter.IsShowMoreExpanded = filter.hasMoreThanLimit
				? this.getSavedShowMoreOpenState(filter.Name)
				: false;

			const template = document.getElementById(filter.Template + 'Template').innerHTML;
			filtersHTML += Mustache.render(template, filter);
		}

		const filtersElement = this.filtersFormElement.querySelector('.filters');
		if (filtersElement) {
			filtersElement.innerHTML = filtersHTML;
			this.hideOverLimitFilterItems();
		}

		this.templateAppliedFilters(data);
		this.setRangeFilterButtonState();
	}

	private templateAppliedFilters(data: any) {
		const appliedFiltersTemplate = document.getElementById('AppliedFiltersTemplate').innerHTML;
		const appliedFiltersElement = document.getElementById('appliedFilters');
		if (appliedFiltersElement) {
			appliedFiltersElement.innerHTML = Mustache.render(appliedFiltersTemplate, data);
		}
	}

	private setRangeFilterButtonState() {
		const rangeFilters = this.filtersFormElement.querySelectorAll('.rangeFilter');
		rangeFilters.forEach((rangeFilter: HTMLElement) => {
			const minInput = rangeFilter.querySelector('.js-min-filter') as HTMLInputElement;
			const maxInput = rangeFilter.querySelector('.js-max-filter') as HTMLInputElement;
			const applyButton = rangeFilter.querySelector('.js-apply-range-filter') as HTMLButtonElement;

			if (minInput && maxInput && applyButton) {
				const hasMinValue = minInput.value.trim() !== '';
				const hasMaxValue = maxInput.value.trim() !== '';

				if (hasMinValue || hasMaxValue) {
					let isValid = true;

					if (hasMinValue) {
						isValid = this.validateRangeInputs(minInput) && isValid;
					}
					if (hasMaxValue) {
						isValid = this.validateRangeInputs(maxInput) && isValid;
					}

					if (isValid) {
						applyButton.disabled = false;
						applyButton.classList.remove('is-faded');
					} else {
						applyButton.classList.add('is-faded');
						applyButton.disabled = true;
					}
				} else {
					applyButton.classList.add('is-faded');
					applyButton.disabled = true;
				}
			}
		});
	}

	private setMinValues(filter: any, parentFilter: any) {
		if (filter.Id) {
			let currentValue = this.getRangeValueFromQueryString(this.minFilterPrefix + parentFilter.Name);
			let id = filter.Id;

			if (filter.Values && filter.Values.length > 0) {
				const selectedValue = filter.Values.find((value: any) => value.IsSelected === true);
				if (selectedValue && selectedValue.Value) {
					currentValue = selectedValue.Value.toString();
				}
				id = filter.Values[0].Id;
			}

			parentFilter.MinValue = currentValue;
			parentFilter.MinId = id;
			parentFilter.MinIdDesktop = id + '--desktop';
			parentFilter.MinIdMobile = id + '--mobile';
		}
	}

	private setMaxValues(filter: any, parentFilter: any) {
		if (filter.Id) {
			let currentValue = this.getRangeValueFromQueryString(this.maxFilterPrefix + parentFilter.Name);
			let id = filter.Id;

			if (filter.Values && filter.Values.length > 0) {
				const selectedValue = filter.Values.find((value: any) => value.IsSelected === true);
				if (selectedValue && selectedValue.Value) {
					currentValue = selectedValue.Value.toString();
				}
				id = filter.Values[0].Id;
			}

			parentFilter.MaxValue = currentValue;
			parentFilter.MaxId = id;
			parentFilter.MaxIdDesktop = id + '--desktop';
			parentFilter.MaxIdMobile = id + '--mobile';
		}
	}

	private matchingFilter(parentFilterName: string, filters: any) {
		return filters.find(f => f.Name === parentFilterName);
	}

	private filterInfo(filter: any) {
		let isMin = filter.Name.startsWith(this.minFilterPrefix);
		let isMax = filter.Name.startsWith(this.maxFilterPrefix)
		return {
			isMinMax: isMin || isMax,
			isMin: isMin,
			isMax: isMax
		}
	}

	private markZeroResultFilters(data: any) {
		if (!data.Filters) {
			return;
		}

		for (let i = 0; i < data.Filters.length; i++) {
			const filter = data.Filters[i];
			if (filter.Values && filter.Values.length > 0) {
				for (let j = 0; j < filter.Values.length; j++) {
					filter.Values[j].isZeroResult = filter.Values[j].Hits === 0;
					filter.Values[j].disabled = filter.Values[j].isZeroResult && !filter.Values[j].IsSelected;
				}
			}

			if (filter.ValuesLookup && typeof filter.ValuesLookup === 'object') {
				Object.values(filter.ValuesLookup).forEach((value: any) => {
					value.isZeroResult = value.Hits === 0;
					value.disabled = value.isZeroResult && !value.IsSelected;
				});
			}
		}
	}

	private removeLastFilter() {
		if (this.lastChangedInput) {
			if (this.lastChangedInput.type === 'checkbox' || this.lastChangedInput.type === 'radio') {
				this.lastChangedInput.checked = false;
				this.lastChangedInput.removeAttribute('checked');
			} else if (this.lastChangedInput.type === 'number') {
				this.lastChangedInput.value = '';
				this.removeRangeFilter(this.lastChangedInput);
			} else {
				this.lastChangedInput.value = '';
			}

			this.lastChangedInput = null;
		}
	}

	private removeRangeFilter(input: HTMLInputElement) {
		const name = input.getAttribute('name');
		const params = new URLSearchParams(window.location.search);
		params.delete(name);
		const updatedUrl = `${location.pathname}?${params.toString()}`;
		window.history.replaceState({}, '', updatedUrl);
	}

	private removeErrors() {
		const errorElements = document.querySelectorAll('.js-filters-error, .js-filters-no-results');
		Helpers.scopedLoop(errorElements, (currentItem: HTMLElement, i: Number, context: any) => {
			if (currentItem) {
				fastdom.mutate(() => {
					if (currentItem.parentNode) {
						currentItem.parentNode.removeChild(currentItem);
					}
				});
			}
		}, this);

		this.isShowingNoResults = false;

		fastdom.mutate(() => {
			const pagerCountElements = document.querySelectorAll('.c-pager__count');
			const loadElements = document.querySelectorAll('.c-load');

			pagerCountElements.forEach(element => {
				(element as HTMLElement).classList.remove('u-hide');
			});

			loadElements.forEach(element => {
				(element as HTMLElement).classList.remove('u-hide');
			});
		});
	}

	private validateRangeInputs(input: HTMLInputElement, updateMessage = true): boolean {
		const inputName = input.getAttribute('name');
		const isMin = input.classList.contains('js-min-filter');
		const isMax = input.classList.contains('js-max-filter');

		if (!isMin && !isMax) {
			return true;
		}

		const baseFilterName = inputName
			.replace(this.minFilterPrefix, '')
			.replace(this.maxFilterPrefix, '');

		const rangeWrapper = input.closest('.rangeFilter');
		let rangeValidationMessage: HTMLElement | null = null;

		const minInput = rangeWrapper.querySelector(`input[name="${this.minFilterPrefix}${baseFilterName}"]`) as HTMLInputElement;
		const maxInput = rangeWrapper.querySelector(`input[name="${this.maxFilterPrefix}${baseFilterName}"]`) as HTMLInputElement;
		const applyButton = rangeWrapper.querySelector('.js-apply-range-filter') as HTMLButtonElement;

		if (!minInput || !maxInput || !rangeWrapper) {
			return true;
		}

		const minValue = parseFloat(minInput.value);
		const maxValue = parseFloat(maxInput.value);
		const hasMinValue = minInput.value.trim() !== '';
		const hasMaxValue = maxInput.value.trim() !== '';
		let isValid = true;

		if (updateMessage) {
			fastdom.mutate(() => {
				rangeWrapper.classList.remove('range-validation-error');

				const afterSibling = rangeWrapper.nextElementSibling && rangeWrapper.nextElementSibling.classList.contains('range-validation-error-message') ? rangeWrapper.nextElementSibling as HTMLElement : null;
				if (afterSibling && afterSibling.parentNode) {
					afterSibling.parentNode.removeChild(afterSibling);
					rangeValidationMessage = null;
				}
			});
		}

		// Check if min value is greater than max value when both are provided
		if (!isNaN(minValue) && !isNaN(maxValue) && minValue > maxValue) {
			const messageText = '*Minimum value cannot be greater than maximum value.';
			isValid = false;

			if (updateMessage) {

				fastdom.mutate(() => {
					rangeWrapper.classList.add('range-validation-error');

					const afterSibling = rangeWrapper.nextElementSibling && rangeWrapper.nextElementSibling.classList.contains('range-validation-error-message') ? rangeWrapper.nextElementSibling as HTMLElement : null;
					rangeValidationMessage = afterSibling;

					let variant: string | null = null;

					if (!variant) {
						rangeWrapper.classList.forEach((c: string) => {
							if (!variant) {
								if (c.endsWith('--desktop')) variant = 'desktop';
								else if (c.endsWith('--mobile')) variant = 'mobile';
							}
						});
					}

					const variantClass = `range-validation-error-message--${variant}`;

					if (!rangeValidationMessage) {
						rangeValidationMessage = document.createElement('div') as HTMLElement;
						rangeValidationMessage.className = 'range-validation-error-message';
						rangeValidationMessage.setAttribute('role', 'alert');
						rangeValidationMessage.setAttribute('aria-live', 'assertive');

						if (rangeWrapper.parentNode) {
							rangeWrapper.parentNode.insertBefore(rangeValidationMessage, rangeWrapper.nextSibling);
						}
					}

					rangeValidationMessage.classList.remove('range-validation-error-message--desktop', 'range-validation-error-message--mobile');
					rangeValidationMessage.classList.add(variantClass);

					rangeValidationMessage.textContent = messageText;
				});
			}
		}

		// Update button state based on validation and input values
		fastdom.mutate(() => {
			if (applyButton) {
				const shouldDisable = !isValid || (!hasMinValue && !hasMaxValue);
				if (shouldDisable) {
					applyButton.classList.add('is-faded');
					applyButton.disabled = true;
				} else {
					applyButton.classList.remove('is-faded');
					applyButton.disabled = false;
				}
			}
		});

		return isValid;
	}

	private isFormValid(): boolean {
		const rangeInputs = this.filtersFormElement.querySelectorAll('.js-min-filter, .js-max-filter') as NodeListOf<HTMLInputElement>;
		let isValid = true;

		rangeInputs.forEach((input: HTMLInputElement) => {
			if (input.value.trim() !== '') {
				if (!this.validateRangeInputs(input)) {
					isValid = false;
				}
			}
		});

		return isValid;
	}

	private setFilterTagsPosition() {

		const appliedFiltersElement = document.querySelector('.js-applied-filters-container') as HTMLElement;

		if (!appliedFiltersElement) {
			return;
		}

		const filtersInMain = !this.filtersFormElement.querySelector('.js-applied-filters-container');

		if (this.isDesktopLayout()) {
			if (filtersInMain) {
				this.prependTo(appliedFiltersElement, this.filtersFormElement);
			}
		} else {
			if (!filtersInMain) {
				this.prependTo(appliedFiltersElement, document.querySelector('#appliedFiltersWrapper'));
			}
		}

		const appliedFiltersInner = document.querySelector('.js-applied-filters-inner');

		if (appliedFiltersInner) {
			appliedFiltersInner.classList.add('mFilterTags--initialized');
		}

	}

	private isDesktopLayout() {
		return matchMedia('(min-width: 980px)').matches;
	}

	private prependTo(containerToAppend: HTMLElement, targetContainer: HTMLElement) {
		fastdom.mutate(() => {
			if (containerToAppend.parentElement) {
				containerToAppend.parentElement.removeChild(containerToAppend);
			}
			targetContainer.insertBefore(containerToAppend, targetContainer.firstChild)
		});
	}

	private scrollToTop() {

		const scrollOffset = Helpers.getOffset(document.querySelector('.js-filter-page'));
		if (site.win.scrollTop() > scrollOffset) {
			window.scrollTo({
				top: scrollOffset.top,
				left: 0,
				behavior: 'smooth'
			});
		}

	}

	private getRangeValueFromQueryString(fieldName: string) {
		const value = new URLSearchParams(window.location.search).get(fieldName);
		return value || '';
	}

	private rehydrateUntouchedRangeInputsFromQueryString(excludedFieldNames: (string | null)[] = []) {
		const excludedNames = new Set(excludedFieldNames.filter((fieldName): fieldName is string => !!fieldName));
		const rangeInputs = this.filtersFormElement.querySelectorAll('.js-min-filter, .js-max-filter') as NodeListOf<HTMLInputElement>;

		rangeInputs.forEach((input) => {
			if (!input.name || excludedNames.has(input.name) || input.value) {
				return;
			}

			const valueFromQueryString = this.getRangeValueFromQueryString(input.name);
			if (!valueFromQueryString) {
				return;
			}

			input.value = valueFromQueryString;
		});
	}

	private serializeFormData() {

		const serialized = [];
		const elements = this.filtersFormElement.querySelectorAll('input,select');
		const urlParams = new URLSearchParams(window.location.search);
		const searchTerm = urlParams.get('term');
		let hasTermInForm = false;

		for (var i = 0; i < elements.length; i++) {

			let field = elements[i] as any;

			if (field.name === 'term') {
				hasTermInForm = true;
			}

			if (field.name === "sort" &&
				!this.sortHasChangedFromDefault &&
				this.initialSearchParams.get('sort') === null) {
				continue;
			}

			if (!field.name ||
				field.disabled ||
				field.type === 'file' ||
				field.type === 'reset' ||
				field.type === 'submit' ||
				field.type === 'button' ||
				field.name === 'expanded' ||
				!field.value
			) {
				continue;
			}

			let isMinMax = field.name.startsWith(this.minFilterPrefix) || field.name.startsWith(this.maxFilterPrefix);
			if (isMinMax && serialized.find(s => s.startsWith(field.name))) {
				continue;
			}

			if (field.type === 'select-multiple') {
				for (var j = 0; j < field.options.length; j++) {
					if (!field.options[j].selected) continue;
					serialized.push(encodeURIComponent(field.name) + "=" + encodeURIComponent(field.options[j].value));
				}

			} else if ((field.type !== 'checkbox' && field.type !== 'radio') || field.checked) {
				serialized.push(encodeURIComponent(field.name) + "=" + encodeURIComponent(field.value));
			}

		}

		if (searchTerm && !hasTermInForm) {
			serialized.push(encodeURIComponent('term') + "=" + encodeURIComponent(searchTerm));
		}

		return serialized.join('&');
	}

	private trackListView(data: any) {
		let items = [];
		for (var i = 0; i < data.Products.length; i++) {
			const currentLineItem = data.Products[i];
			items.push({
				item_id: currentLineItem.Name,
				item_name: currentLineItem.DisplayName,
				price: currentLineItem.Price.Price
			});
		}
		Tracking.trackListView("category_results", "Category Results", items);
	}

	private trackSelectItem(element: HTMLElement) {
		const elementParent = element.closest('.js-product') as HTMLDivElement;
		const item = Tracking.getItemDetails(elementParent);
		const isVariant = elementParent.getAttribute('data-product-has-variants');
		Tracking.trackSelectItem("category_results", "Category Results", item);
		const productPosition = Helpers.getElementIndex(elementParent.closest('.js-limit-list-item')) + 1;
		const queryID = AlgoliaTracking.queryID();
		if (!isVariant) {
			AlgoliaTracking.clickedObjectID(elementParent.getAttribute('data-product-guid'), productPosition, queryID ? 'PLP: Product Clicked After Search' : 'PLP: Product Clicked');
		}
	}

	private hideOverLimitFilterItems() {
		const checkboxesAndRadios = document.querySelectorAll('.formChecks[data-limit], .formRadios[data-limit]');
		
		checkboxesAndRadios.forEach((group) => {
			const limit = parseInt(group.getAttribute('data-limit'), 10);
			const items = group.querySelectorAll('.level1');
			
			if (items.length > limit) {
				// Check if this filter should be expanded based on saved state
				const filterName = group.closest('.js-toggle-block')?.querySelector('[data-toggle-value]')?.getAttribute('data-toggle-value');
				const savedShowMoreState = filterName ? this.getShowMoreState(filterName) : null;
				const shouldBeExpanded = savedShowMoreState !== null ? savedShowMoreState : group.getAttribute('data-expanded') === 'true';
				
				if (!shouldBeExpanded) {
					items.forEach((item: HTMLElement, index: number) => {
						if (index >= limit) {
							item.style.display = 'none';
						}
					});
				}
			}
		});
	}

	private showMoreToggles() {

		const checkboxesAndRadios = SelectorFilter.excludeByClass(document.querySelectorAll('.formChecks[data-limit], .formRadios[data-limit]'), 'moreDone');

		for (let i = 0; i < checkboxesAndRadios.length; i++) {

			const group = checkboxesAndRadios[i];
			const limit = parseInt(group.getAttribute('data-limit'), 10);
			const offset = limit + 0;
			const items = group.querySelectorAll('.level1');

			if (items.length <= offset) {
				continue;
			}

			const filterName = group.closest('.js-toggle-block')?.querySelector('[data-toggle-value]')?.getAttribute('data-toggle-value');
			const savedShowMoreState = filterName ? this.getShowMoreState(filterName) : null;
			const shouldBeExpanded = savedShowMoreState !== null ? savedShowMoreState : group.getAttribute('data-expanded') === 'true';
			const showMoreButton = group.nextElementSibling as HTMLElement;
			
			if (showMoreButton && showMoreButton.hasAttribute('data-show-more-toggle')) {
				const link = showMoreButton.querySelector('[data-show-more-link]') as HTMLElement;

				if (shouldBeExpanded) {
					items.forEach((item: HTMLElement, idx: number) => {
						if (idx >= limit) {
							item.style.display = 'block';
						}
					});
				}

				fastdom.mutate(() => {
					if (link) {
						if (!link.hasAttribute('data-show-more-attached')) {
							this.attachShowMoreLinkEvents(group, items, limit, showMoreButton);
							link.setAttribute('data-show-more-attached', 'true');
						}
					}
					
					if (!shouldBeExpanded) {
						showMoreButton.classList.remove('mShowMore--hidden');
						showMoreButton.classList.add('mShowMore--visible');
						this.setShowMoreLinkState(showMoreButton, false);
					} else {
						showMoreButton.classList.remove('mShowMore--hidden');
						showMoreButton.classList.add('mShowMore--visible');
						this.setShowMoreLinkState(showMoreButton, true);
					}
					group.classList.add('moreDone');
				});
			}
		}

	}

	private attachShowMoreLinkEvents(group: HTMLElement, items: NodeListOf<Element>, limit: number, showMoreButton?: HTMLElement) {
		const filterName = group.closest('.js-toggle-block')?.querySelector('[data-toggle-value]')?.getAttribute('data-toggle-value');
		const self = this;
		const wrapper = showMoreButton || (group.parentNode.querySelector('.mShowMore') as HTMLElement);
		const button = wrapper ? (wrapper.querySelector('.link') as HTMLElement) : null;
		if (!button) return;
		
		button.addEventListener('click', function (e) {
			e.preventDefault();
			const link = e.target as HTMLLinkElement;
			
			fastdom.mutate(() => {
				if (link.classList.contains('mShowMore--expanded')) {
					items.forEach((item: HTMLElement, i: number) => {
						if (i >= limit) {
							item.style.display = 'none';
						}
					});
					self.setShowMoreLinkState(wrapper, false);
					if (filterName) {
						self.saveShowMoreState(filterName, false);
					}
				} else {
					items.forEach((item: HTMLElement, i: number) => {
						if (i >= limit) {
							item.style.display = 'block';
						}
					});
					self.setShowMoreLinkState(wrapper, true);
					if (filterName) {
						self.saveShowMoreState(filterName, true);
					}
				}
			});
		});
	}

	private getShowMoreLabels(container: HTMLElement): { more: string; less: string } {
		const labelsElement = container.querySelector('[data-show-more-link]') as HTMLElement;
		return {
			more: labelsElement?.getAttribute('data-show-more-label'),
			less: labelsElement?.getAttribute('data-show-less-label')
		};
	}

	private setShowMoreLinkState(container: HTMLElement, isExpanded: boolean) {
		const link = container.querySelector('[data-show-more-link]') as HTMLElement;
		if (!link) {
			return;
		}

		const labels = this.getShowMoreLabels(container);
		link.textContent = isExpanded ? labels.less : labels.more;
		link.classList.toggle('mShowMore--expanded', isExpanded);
		link.classList.toggle('less', isExpanded);
	}

	private saveShowMoreState(filterName: string, isExpanded: boolean) {
		this.showMoreStates[filterName] = isExpanded;
	}

	private getShowMoreState(filterName: string): boolean | null {
		return this.showMoreStates[filterName] !== undefined ? this.showMoreStates[filterName] : null;
	}

	private getSavedShowMoreOpenState(filterName: string): boolean {
		const mobileState = this.mobileFiltersState.find((filterState) => filterState.name === filterName);
		if (mobileState) {
			return mobileState.showMoreOpen === true;
		}

		return this.getShowMoreState(filterName) === true;
	}

	private updateCanonicalLink(page: number) {
		const canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
		if (canonicalLink) {
			const canonicalUrl = new URL(canonicalLink.href);
			if (page === 1) {
				canonicalUrl.searchParams.delete('page');
			} else {
				canonicalUrl.searchParams.set('page', page.toString());
			}

			canonicalLink.href = canonicalUrl.toString();
		}
	}

	private updateRelNextAndPrevious(currentPage: number, hasNextPage: boolean, hasPreviousPage: Boolean) {
		const head = document.head;
		const prevSelector = 'link[rel="prev"]';
		const nextSelector = 'link[rel="next"]';
		const currentBaseURL = new URL(window.location.origin + window.location.pathname);

		if (!hasPreviousPage) {
			const prevLink = head.querySelector(prevSelector);
			if (prevLink) {
				prevLink.remove();
			}
		} else {
			let prevLink = head.querySelector(prevSelector) as HTMLLinkElement;
			const prevPage = currentPage - 1;
			const prevUrl = currentBaseURL;
			if (prevPage === 1) {
				prevUrl.searchParams.delete("page");
			} else {
				prevUrl.searchParams.set("page", prevPage.toString());
			}
			if (!prevLink) {
				prevLink = document.createElement("link");
				prevLink.rel = "prev";
				prevLink.setAttribute("data-test", "meta-previous");
				head.appendChild(prevLink);
			}
			prevLink.href = prevUrl.toString();
		}

		let nextLink = head.querySelector(nextSelector) as HTMLLinkElement;
		const nextPage = currentPage + 1;
		if (hasNextPage) {
			const nextUrl = currentBaseURL;
			nextUrl.searchParams.set("page", nextPage.toString());
			if (!nextLink) {
				nextLink = document.createElement("link");
				nextLink.rel = "next";
				nextLink.setAttribute("data-test", "meta-next");
				head.appendChild(nextLink);
			}
			nextLink.href = nextUrl.toString();
		} else if (nextLink) {
			nextLink.remove();
		}
	}

}
